import * as React from 'react';
import { Box, Button, Link, useMediaQuery } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import dashboardTheme, { tokens, WIDE_LAYOUT_QUERY } from '../../Components/DashboardTheme/DashboardTheme';
import { Kicker, BodyText, TeamVivenzioTitle } from '../../Components/Typography/Typography';
import Reveal from '../../Components/Reveal/Reveal';
import Ticker from '../../Components/Ticker/Ticker';
import { oneChampionship } from './OneChampionshipData';
import oneImg from '../../img/valentina-one-championship.webp';

export default function OneChampionship() {
    const isWide = useMediaQuery(WIDE_LAYOUT_QUERY);

    return (
        <Box component="section" id="oneChampionshipSection" sx={{ backgroundColor: tokens.ink }}>
            <Ticker items={oneChampionship.ticker} />

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    px: { xs: 3, md: 8 },
                    py: { xs: 8, md: 14 },
                }}
            >
                {/* Red bloom behind the content */}
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: '-20%',
                        right: '-10%',
                        width: { xs: 420, md: 760 },
                        height: { xs: 420, md: 760 },
                        background: `radial-gradient(circle, ${tokens.red}33 0%, transparent 68%)`,
                        pointerEvents: 'none',
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        display: 'grid',
                        gridTemplateColumns: isWide ? '1.15fr 0.85fr' : '1fr',
                        gap: { xs: 5, md: 8 },
                        alignItems: 'center',
                        maxWidth: 1280,
                        mx: 'auto',
                    }}
                >
                    {/* ---------- Left: the announcement ---------- */}
                    <Box>
                        <Reveal>
                            <Kicker justifyContent="flex-start">{oneChampionship.kicker}</Kicker>

                            <TeamVivenzioTitle
                                textAlign="left"
                                paddingTop={2}
                                fontSize={{ xs: 52, sm: 78, md: 104 }}
                                color={tokens.white}
                            >
                                {oneChampionship.title}
                            </TeamVivenzioTitle>

                            <Box
                                sx={{
                                    fontFamily: tokens.heading,
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    fontSize: { xs: 20, md: 28 },
                                    lineHeight: 1.2,
                                    color: tokens.red,
                                    mt: 1,
                                    mb: 3,
                                }}
                            >
                                {oneChampionship.headline}
                            </Box>

                            <BodyText textAlign="left" maxWidth={620}>
                                {oneChampionship.lead}
                            </BodyText>
                        </Reveal>

                        {/* ---------- Stats ---------- */}
                        <Reveal delay={120}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    borderTop: `1px solid ${tokens.line}`,
                                    borderBottom: `1px solid ${tokens.line}`,
                                    mt: 5,
                                }}
                            >
                                {oneChampionship.stats.map((stat, index) => (
                                    <Box
                                        key={stat.label}
                                        sx={{
                                            py: { xs: 2.5, md: 3.5 },
                                            px: { xs: 1, md: 2 },
                                            borderLeft: index === 0 ? 'none' : `1px solid ${tokens.line}`,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                fontFamily: tokens.display,
                                                fontSize: { xs: 36, md: 52 },
                                                lineHeight: 1,
                                                color: tokens.gold,
                                            }}
                                        >
                                            {stat.value}
                                        </Box>
                                        <Box
                                            sx={{
                                                fontFamily: tokens.heading,
                                                fontSize: { xs: 11, md: 13 },
                                                letterSpacing: '0.18em',
                                                textTransform: 'uppercase',
                                                color: tokens.dim,
                                                mt: 1,
                                            }}
                                        >
                                            {stat.label}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Reveal>

                        {/* ---------- Actions ---------- */}
                        <Reveal delay={200}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4.5 }}>
                                <Button
                                    variant="contained"
                                    href={oneChampionship.athlete.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    endIcon={<ArrowOutwardIcon />}
                                    sx={{
                                        backgroundColor: tokens.red,
                                        color: '#fff',
                                        px: 3.5,
                                        py: 1.5,
                                        transition: `all 0.4s ${tokens.ease}`,
                                        ':hover': {
                                            backgroundColor: tokens.redDeep,
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 12px 32px ${dashboardTheme.palette.customColors.red}55`,
                                        },
                                    }}
                                >
                                    {oneChampionship.athlete.name}
                                </Button>

                                <Button
                                    variant="outlined"
                                    href={oneChampionship.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: tokens.white,
                                        borderColor: tokens.lineStrong,
                                        px: 3.5,
                                        py: 1.5,
                                        transition: `all 0.4s ${tokens.ease}`,
                                        ':hover': {
                                            borderColor: tokens.white,
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    Segui il percorso
                                </Button>
                            </Box>
                        </Reveal>

                        {/* ---------- Camps ---------- */}
                        <Reveal delay={260}>
                            <Box sx={{ mt: 5 }}>
                                <Box
                                    sx={{
                                        fontFamily: tokens.heading,
                                        fontSize: 12,
                                        letterSpacing: '0.28em',
                                        textTransform: 'uppercase',
                                        color: tokens.dim,
                                        mb: 1.5,
                                    }}
                                >
                                    Camp in Thailandia
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {oneChampionship.camps.map((camp) => (
                                        <Box
                                            key={camp.name}
                                            component={camp.link ? Link : 'span'}
                                            {...(camp.link
                                                ? { href: camp.link, target: '_blank', rel: 'noopener noreferrer', underline: 'none' }
                                                : {})}
                                            sx={{
                                                fontFamily: tokens.heading,
                                                fontSize: { xs: 13, md: 14 },
                                                letterSpacing: '0.12em',
                                                textTransform: 'uppercase',
                                                color: tokens.muted,
                                                border: `1px solid ${tokens.line}`,
                                                px: 2,
                                                py: 0.9,
                                                transition: `all 0.35s ${tokens.ease}`,
                                                ':hover': {
                                                    color: tokens.white,
                                                    borderColor: tokens.red,
                                                },
                                            }}
                                        >
                                            {camp.name}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Reveal>
                    </Box>

                    {/* ---------- Right: framed image ---------- */}
                    <Reveal delay={160}>
                            <Box sx={{ position: 'relative', pl: { md: 2 } }}>
                                {/* Offset outline frame */}
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        transform: 'translate(18px, 18px)',
                                        border: `1px solid ${tokens.red}`,
                                        pointerEvents: 'none',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'relative',
                                        // 3:2 matches the source, so the shot is shown
                                        // uncropped — the ONE kit and the Vivenzio Gym
                                        // logo on the shorts are the point of it.
                                        aspectRatio: '3 / 2',
                                        overflow: 'hidden',
                                        ':hover img': { transform: 'scale(1.04)' },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={oneImg}
                                        alt={oneChampionship.image.alt}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            filter: 'contrast(1.05) brightness(0.94)',
                                            transition: `transform 1s ${tokens.ease}`,
                                        }}
                                    />
                                </Box>

                                {/* Caption sits under the photo rather than over it,
                                    so nothing covers the kit. */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        backgroundColor: tokens.surface,
                                        borderTop: `2px solid ${tokens.red}`,
                                        px: 3,
                                        py: 2.25,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            fontFamily: tokens.display,
                                            fontSize: 30,
                                            lineHeight: 1,
                                            color: tokens.white,
                                        }}
                                    >
                                        {oneChampionship.image.caption}
                                    </Box>
                                    <Box
                                        sx={{
                                            fontFamily: tokens.heading,
                                            fontSize: 12,
                                            letterSpacing: '0.2em',
                                            textTransform: 'uppercase',
                                            color: tokens.red,
                                            mt: 0.75,
                                        }}
                                    >
                                        {oneChampionship.image.subCaption}
                                    </Box>
                                </Box>
                        </Box>
                    </Reveal>
                </Box>
            </Box>
        </Box>
    );
}
